/**
 * 權限目錄（權限管理 — 切片 1，唯讀）。
 *
 * 對應 BE GET /admin/permissions。權限由程式碼 @RequirePermissions 定義，
 * 為契約來源 → 本頁唯讀，不提供增刪。角色↔權限編輯見角色詳情頁（切片 2）。
 */

import { useQuery } from '@tanstack/react-query';

import { Button } from '../components/Button';
import { getPermissionCatalog, type RbacPermissionModule } from '../lib/api-client';

export function PermissionsCatalogPage(): JSX.Element {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin.permissions'],
    queryFn: getPermissionCatalog,
  });

  const modules: RbacPermissionModule[] = data?.modules ?? [];

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">權限目錄</h1>
        <div className="page-head__actions">
          <Button variant="secondary" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? '更新中…' : '重新整理'}
          </Button>
        </div>
      </div>

      <p className="page-intro">
        系統權限點清單，依 module 分組。權限由後端程式碼定義（為授權契約來源），此頁唯讀；
        角色擁有哪些權限請至「角色管理」檢視與編輯。
      </p>

      {isLoading && <div>載入中…</div>}
      {error && (
        <div className="error-banner">
          {(error as Error).message}
          <div style={{ marginTop: 4, fontSize: 12 }}>
            提醒：此頁需 superadmin / ticketing_admin token（admin.audit_log 權限）。
          </div>
        </div>
      )}

      {data && (
        <>
          {modules.map((m) => (
            <div key={m.module} style={{ marginBottom: 16 }}>
              <h2 style={{ fontSize: 15, margin: '0 0 6px 0' }}>
                module：{m.module}
                <span style={{ color: 'var(--c-text-muted)', fontWeight: 400, marginLeft: 8 }}>
                  （{m.permissions.length} 項）
                </span>
              </h2>
              <div className="table-card">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: '40%' }}>權限 key</th>
                      <th>顯示名稱</th>
                    </tr>
                  </thead>
                  <tbody>
                    {m.permissions.map((p) => (
                      <tr key={p.id}>
                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.key}</td>
                        <td>{p.display_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          <div className="list-foot">
            <div>
              共 {data.total} 項權限，{modules.length} 個 module
            </div>
          </div>
        </>
      )}
    </div>
  );
}
